import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { AuthShell } from "./Login";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2 } from "lucide-react";

export default function Join() {
  const { signupInvite } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ invite_code: "", name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [pending, setPending] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const res = await signupInvite({ ...form, invite_code: form.invite_code.toUpperCase() });
      if (res.pending) {
        setPending(true);
        toast.success("Account created — awaiting admin approval");
      }
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail, "Join failed"));
    } finally { setBusy(false); }
  };

  if (pending) {
    return (
      <AuthShell>
        <div className="overline mb-2">SITESYNC / PENDING APPROVAL</div>
        <h1 className="text-3xl font-semibold tracking-tighter mb-6">Awaiting company admin.</h1>
        <div className="border border-border rounded-sm p-5 text-sm bg-card">
          Your account for <strong>{form.email}</strong> has been created.
          A company administrator must approve access before you can sign in.
        </div>
        <div className="mt-6">
          <Button onClick={() => navigate("/login")} variant="outline" className="rounded-sm border-border" data-testid="pending-back-to-login">
            Back to login
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="overline mb-2">SITESYNC / JOIN COMPANY</div>
      <h1 className="text-3xl font-semibold tracking-tighter mb-8">Enter your invite code.</h1>
      <form onSubmit={submit} className="space-y-4" data-testid="join-form">
        <div>
          <Label className="overline mb-2 block">Invite Code</Label>
          <Input
            required
            value={form.invite_code}
            onChange={upd("invite_code")}
            placeholder="e.g. 3F2A1B"
            className="rounded-sm font-mono uppercase tracking-widest"
            data-testid="join-code-input"
          />
        </div>
        <div>
          <Label className="overline mb-2 block">Full Name</Label>
          <Input required value={form.name} onChange={upd("name")} className="rounded-sm font-mono" data-testid="join-name-input" />
        </div>
        <div>
          <Label className="overline mb-2 block">Work Email</Label>
          <Input required type="email" value={form.email} onChange={upd("email")} className="rounded-sm font-mono" data-testid="join-email-input" />
        </div>
        <div>
          <Label className="overline mb-2 block">Password</Label>
          <Input required type="password" minLength={6} value={form.password} onChange={upd("password")} className="rounded-sm font-mono" data-testid="join-password-input" />
        </div>
        {err && <div className="text-xs text-destructive font-mono border border-destructive/40 bg-destructive/5 px-3 py-2 rounded-sm">{err}</div>}
        <Button type="submit" disabled={busy} className="w-full rounded-sm h-11 bg-primary hover:bg-primary/90" data-testid="join-submit-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request access"}
        </Button>
      </form>
      <div className="mt-6 text-sm text-muted-foreground">
        Have an account? <Link to="/login" className="text-foreground hover:text-primary">Sign in</Link>
      </div>
    </AuthShell>
  );
}
